import React, { useState, useEffect } from 'react';
import { useAuth, api, useTheme } from '../App';

const TABS = [
  { id: 'voice',      label: 'Voice Profile', icon: '◎' },
  { id: 'company',    label: 'Firmenprofil',  icon: '🏢' },
  { id: 'channels',   label: 'Kanäle',        icon: '⊹' },
  { id: 'cms',        label: 'Blog-CMS',      icon: '⬡' },
  { id: 'analytics',  label: 'Analytics',     icon: '◈' },
  { id: 'appearance', label: 'Darstellung',   icon: '◐' },
  { id: 'account',    label: 'Account',       icon: '⊙' },
];

const PLATFORMS_INFO = {
  instagram:       { label: 'Instagram',   color: '#e1306c', icon: '◈' },
  facebook:        { label: 'Facebook',    color: '#1877f2', icon: 'f'  },
  linkedin:        { label: 'LinkedIn',    color: '#0077b5', icon: 'in' },
  tiktok:          { label: 'TikTok',      color: '#69c9d0', icon: '♪' },
  youtube:         { label: 'YouTube',     color: '#ff0000', icon: '▶' },
  x:               { label: 'X',           color: '#555555', icon: 'X'  },
  threads:         { label: 'Threads',     color: '#888888', icon: '@'  },
  pinterest:       { label: 'Pinterest',   color: '#e60023', icon: 'P'  },
  reddit:          { label: 'Reddit',      color: '#ff4500', icon: 'R'  },
  bluesky:         { label: 'Bluesky',     color: '#0085ff', icon: '☁' },
  google_business: { label: 'Google',      color: '#ea4335', icon: 'G'  },
};

export default function Settings() {
  const { token, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [tab, setTab] = useState('voice');
  const isDark = theme === 'dark';

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t && TABS.find(x => x.id === t)) setTab(t);
  }, []);

  return (
    <div style={{ padding: '24px', maxWidth: 900 }}>
      <div className="fade-up" style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
          <span className="gradient-text">Settings</span>
        </h1>
        <p style={{ color: 'var(--text2)', fontSize: 13 }}>Konfiguriere dein Content Tool.</p>
      </div>

      <div className="fade-up" style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap', animationDelay: '0.05s' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 16px', borderRadius: 10, fontSize: 13,
            fontWeight: tab === t.id ? 600 : 400, fontFamily: 'Outfit', cursor: 'pointer',
            background: tab === t.id ? 'linear-gradient(135deg, var(--cyan-dim), var(--blue-dim))' : 'var(--glass)',
            color: tab === t.id ? 'var(--cyan)' : 'var(--text2)',
            border: `1px solid ${tab === t.id ? 'rgba(0,255,255,0.25)' : 'var(--glass-border)'}`,
            transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6
          }}><span>{t.icon}</span> {t.label}</button>
        ))}
      </div>

      {tab === 'voice'      && <VoiceTab token={token} />}
      {tab === 'company'    && <CompanyTab token={token} />}
      {tab === 'channels'   && <ChannelsTab token={token} />}
      {tab === 'cms'        && <CmsTab token={token} />}
      {tab === 'analytics'  && <AnalyticsTab token={token} />}
      {tab === 'appearance' && <AppearanceTab isDark={isDark} toggleTheme={toggleTheme} />}
      {tab === 'account'    && <AccountTab user={user} token={token} />}
    </div>
  );
}

// ── Voice Profile Tab ─────────────────────────────────────────────────────────
function VoiceTab({ token }) {
  const [profile, setProfile] = useState(null);
  const [rawPosts, setRawPosts] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/profile', {}, token).then(d => { if (d) { setProfile(d); setRawPosts(d.raw_posts || ''); } }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  async function handleSave() {
    if (rawPosts.trim().length < 50) { setError('Bitte mehr Text eingeben.'); return; }
    setSaving(true); setError(''); setSuccess(false);
    try {
      const d = await api('/profile', { method: 'POST', body: JSON.stringify({ raw_posts: rawPosts }) }, token);
      setProfile(d); setSuccess(true); setTimeout(() => setSuccess(false), 3000);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  if (loading) return <div className="shimmer" style={{ height: 300, borderRadius: 12 }} />;

  return (
    <div className="fade-in">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 18 }}>
        {[['01','Posts einfügen','5–10 alte Beiträge als Text'],['02','KI analysiert','Stil, Ton und Struktur'],['03','Immer deine Stimme','Klingt nach dir, nicht KI']].map(([n,t,s]) => (
          <div key={n} className="glass" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 18, fontFamily: 'Orbitron', fontWeight: 800, color: 'var(--cyan)', opacity: 0.5, marginBottom: 6 }}>{n}</div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{t}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>{s}</div>
          </div>
        ))}
      </div>
      <div className="glass" style={{ padding: 22, marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.08em', marginBottom: 8 }}>DEINE ALTEN POSTS</label>
        <textarea value={rawPosts} onChange={e => setRawPosts(e.target.value)}
          placeholder={`Post 1:\nText des ersten Posts...\n\nPost 2:\nText des zweiten Posts...`}
          rows={10} className="input" style={{ lineHeight: 1.75, resize: 'vertical', fontSize: 13 }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, flexWrap: 'wrap', gap: 10 }}>
          <div>
            {error && <p style={{ color: 'var(--red)', fontSize: 12 }}>{error}</p>}
            {success && <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span className="dot-live" /><p style={{ color: 'var(--green)', fontSize: 12 }}>Profil gespeichert.</p></div>}
          </div>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ fontSize: 13 }}>
            {saving ? <><Spinner /> Analysiere...</> : profile ? '↺ Aktualisieren' : '▶ Profil erstellen'}
          </button>
        </div>
      </div>
      {profile?.profile_text && (
        <div className="glass-intense fade-in">
          <div style={{ height: 2, background: 'linear-gradient(90deg, var(--green), transparent)' }} />
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span className="dot-live" />
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--green)', letterSpacing: '0.08em' }}>ANALYSIERTER SCHREIBSTIL</span>
            </div>
            <p style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{profile.profile_text}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Firmenprofil Tab ──────────────────────────────────────────────────────────
function CompanyTab({ token }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ company_name: '', industry: '', target_audience: '', tone: '', keywords: '', location: '', description: '' });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoData,      setLogoData]      = useState(null);
  const logoInputRef = React.useRef(null);

  useEffect(() => {
    api('/company', {}, token).then(d => {
      if (d) {
        setProfile(d);
        setLogoData(d.logo_data || null);
        setForm({ company_name: d.company_name||'', industry: d.industry||'', target_audience: d.target_audience||'', tone: d.tone||'', keywords: d.keywords||'', location: d.location||'', description: d.description||'' });
      }
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res  = await fetch('/api/company/logo', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload fehlgeschlagen');
      setLogoData(data.logo_data);
      setSuccess('Logo gespeichert. Wird automatisch auf generierten Bildern eingeblendet.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) { setError(err.message); }
    finally { setLogoUploading(false); e.target.value = ''; }
  }

  async function handleLogoDelete() {
    if (!window.confirm('Logo löschen?')) return;
    try { await api('/company/logo', { method: 'DELETE' }, token); setLogoData(null); }
    catch (err) { setError(err.message); }
  }

  const filledFields = [form.company_name, form.industry, form.target_audience, form.location, form.description, form.tone].filter(v => v?.trim()).length;
  const canGenerate = form.company_name?.trim() && filledFields >= 3;

  async function handleSave() {
    if (!form.company_name.trim()) { setError('Firmenname ist Pflicht.'); return; }
    setSaving(true); setError(''); setSuccess('');
    try { const d = await api('/company', { method: 'POST', body: JSON.stringify(form) }, token); setProfile(d); setSuccess('Firmendaten gespeichert.'); setTimeout(() => setSuccess(''), 3000); }
    catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleGenerate() {
    if (profile?.profile_text && !window.confirm('Neu generieren kostet API-Credits. Fortfahren?')) return;
    setGenerating(true); setError(''); setSuccess('');
    try {
      await api('/company', { method: 'POST', body: JSON.stringify(form) }, token);
      const d = await api('/company/generate', { method: 'POST' }, token);
      setProfile(d); setSuccess('KI-Profil erfolgreich generiert.');
    } catch (e) { setError(e.message); } finally { setGenerating(false); }
  }

  const fields = [
    { key: 'company_name',    label: 'Firmenname *',      placeholder: 'look4more',                     req: true  },
    { key: 'industry',        label: 'Branche *',          placeholder: 'z.B. Local SEO, Webdesign',     req: true  },
    { key: 'target_audience', label: 'Zielgruppe *',       placeholder: 'z.B. Lokale Unternehmen, KMU', req: true  },
    { key: 'location',        label: 'Standort',           placeholder: 'z.B. Neustrelitz, MV',          req: false },
    { key: 'tone',            label: 'Kommunikationsstil', placeholder: 'z.B. direkt, professionell',    req: false },
    { key: 'keywords',        label: 'Keywords',           placeholder: 'z.B. SEO, Sichtbarkeit',        req: false },
  ];

  if (loading) return <div className="shimmer" style={{ height: 200, borderRadius: 12 }} />;

  return (
    <div className="fade-in">
      <div className="glass" style={{ padding: 22, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Firmenprofil</h2>
            <p style={{ fontSize: 12, color: 'var(--text2)' }}>Basis für KI-Themenvorschläge und Post-Generierung.</p>
          </div>
          {profile?.generated_at && <span style={{ fontSize: 10, color: 'var(--green)', padding: '3px 10px', borderRadius: 20, background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)' }}>✓ KI-Profil aktiv</span>}
        </div>
        <div style={{ marginBottom: 18, padding: '10px 14px', background: 'var(--glass2)', borderRadius: 9, border: '1px solid var(--glass-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text2)' }}>Vollständigkeit</span>
            <span style={{ fontSize: 11, color: canGenerate ? 'var(--green)' : 'var(--gold)', fontWeight: 600 }}>{filledFields}/6 Felder</span>
          </div>
          <div style={{ height: 4, background: 'var(--glass-border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(filledFields / 6) * 100}%`, background: canGenerate ? 'var(--green)' : 'var(--gold)', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          {fields.map(f => (
            <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: form[f.key]?.trim() ? 'var(--cyan)' : f.req ? 'var(--gold)' : 'var(--text2)', letterSpacing: '0.08em' }}>
                {f.label.toUpperCase()} {form[f.key]?.trim() ? '✓' : ''}
              </span>
              <input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} className="input" style={{ fontSize: 13 }} />
            </label>
          ))}
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 16 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: form.description?.trim() ? 'var(--cyan)' : 'var(--text2)', letterSpacing: '0.08em' }}>KURZBESCHREIBUNG</span>
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Was macht das Unternehmen konkret?" rows={3} className="input" style={{ resize: 'vertical', fontSize: 13 }} />
        </label>
        {error && <div style={{ padding: '10px 14px', background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.2)', borderRadius: 8, marginBottom: 12 }}><p style={{ color: 'var(--red)', fontSize: 12 }}>{error}</p></div>}
        {success && <p style={{ color: 'var(--green)', fontSize: 12, marginBottom: 10 }}>✓ {success}</p>}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ fontSize: 13 }}>{saving ? <><Spinner /> Speichere...</> : '▶ Speichern'}</button>
          <button onClick={handleGenerate} disabled={generating || !canGenerate} className="btn-ghost" style={{ fontSize: 13, opacity: canGenerate ? 1 : 0.45 }}>
            {generating ? <><Spinner /> Generiere...</> : profile?.profile_text ? '↺ Neu generieren' : '✦ KI-Profil generieren'}
          </button>
        </div>
      </div>
      {/* Logo-Upload */}
      <div className="glass" style={{ padding: 22, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Firmenlogo</h2>
            <p style={{ fontSize: 12, color: 'var(--text2)' }}>Wird automatisch unten rechts auf generierten Bildern eingeblendet.</p>
          </div>
          {logoData && <span style={{ fontSize: 10, color: 'var(--green)', padding: '3px 10px', borderRadius: 20, background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)' }}>✓ Logo aktiv</span>}
        </div>
        {logoData ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 80, height: 80, borderRadius: 10, border: '1px solid var(--glass-border2)', background: 'var(--glass2)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              <img src={logoData} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => logoInputRef.current?.click()} disabled={logoUploading} className="btn-ghost" style={{ fontSize: 12 }}>
                {logoUploading ? <><Spinner /> Lädt...</> : '↻ Ersetzen'}
              </button>
              <button onClick={handleLogoDelete} className="btn-ghost" style={{ fontSize: 12, color: 'var(--red)' }}>✕ Entfernen</button>
            </div>
          </div>
        ) : (
          <button onClick={() => logoInputRef.current?.click()} disabled={logoUploading} className="btn-ghost" style={{ fontSize: 13 }}>
            {logoUploading ? <><Spinner /> Lädt...</> : '📁 Logo hochladen (PNG, WebP, max. 2 MB)'}
          </button>
        )}
        <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} style={{ display: 'none' }} />
        <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>Optimales Format für beste Ergebnisse:</p>
        <ul style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, paddingLeft: 16, lineHeight: 1.8 }}>
          <li>PNG mit transparentem Hintergrund (kein weißer Kasten)</li>
          <li>Mindestgröße: 300 × 100 px (Querformat bevorzugt)</li>
          <li>Maximale Dateigröße: 2 MB</li>
          <li>Helles oder weißes Logo — wird auf dunklen Bildern besser sichtbar</li>
          <li>Kein Schatten, kein äußerer Rahmen</li>
        </ul>
      </div>

      {profile?.profile_text && (
        <div className="glass fade-in">
          <div style={{ height: 2, background: 'linear-gradient(90deg, var(--cyan), transparent)' }} />
          <div style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--cyan)', letterSpacing: '0.08em', marginBottom: 10 }}>KI-GENERIERTES FIRMENPROFIL</div>
            <ProfileTextEditor profile={profile} setProfile={setProfile} token={token} />
            <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 10 }}>Generiert am {new Date(profile.generated_at).toLocaleDateString('de-DE')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Kanäle Tab ────────────────────────────────────────────────────────────────
function ChannelsTab({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setting, setSetting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = () => api('/uploadpost/status', {}, token).then(setStatus).catch(console.error);
  useEffect(() => { loadStatus().finally(() => setLoading(false)); }, [token]);

  async function handleRefresh() { setRefreshing(true); await loadStatus(); setRefreshing(false); }
  async function handleSetup() {
    setSetting(true); setError('');
    try { const d = await api('/uploadpost/setup', { method: 'POST' }, token); window.open(d.access_url, '_blank'); setTimeout(() => loadStatus(), 4000); }
    catch (e) { setError(e.message); } finally { setSetting(false); }
  }

  if (loading) return <div className="shimmer" style={{ height: 300, borderRadius: 12 }} />;
  const connectedAccounts  = status?.accounts || [];
  const connectedPlatforms = status?.platforms || [];

  return (
    <div className="fade-in">
      <div className="glass" style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Social Media Kanäle</h2>
            <p style={{ fontSize: 12, color: 'var(--text2)' }}>Unterstützt 10+ Plattformen.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {status?.connected && <button onClick={handleRefresh} disabled={refreshing} className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }}>{refreshing ? <Spinner /> : '↻ Aktualisieren'}</button>}
            {status?.connected && <span className="dot-live" />}
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 10 }}>UNTERSTÜTZTE PLATTFORMEN</div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {Object.entries(PLATFORMS_INFO).map(([id, p]) => {
              const isConnected = connectedPlatforms.includes(id);
              return (
                <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 20, fontSize: 11, background: isConnected ? `${p.color}18` : 'var(--glass2)', border: `1px solid ${isConnected ? p.color + '55' : 'var(--glass-border)'}`, color: isConnected ? p.color : 'var(--muted)' }}>
                  <span style={{ fontWeight: 800, fontSize: 10 }}>{p.icon}</span>{p.label}{isConnected && <span style={{ fontSize: 9 }}>✓</span>}
                </div>
              );
            })}
          </div>
        </div>
        {status?.connected && connectedAccounts.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 10 }}>VERBUNDENE ACCOUNTS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {connectedAccounts.map((acc, i) => {
                const platform = (acc.platform || '').toLowerCase();
                const info = PLATFORMS_INFO[platform] || { label: platform, color: '#888', icon: '○' };
                const name = acc.display_name || acc.name || acc.handle || platform;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'var(--glass2)', borderRadius: 9, border: `1px solid ${info.color}30` }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: `${info.color}20`, border: `1px solid ${info.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: info.color, fontWeight: 800, flexShrink: 0 }}>{info.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }} className="truncate">{name}</div><div style={{ fontSize: 10, color: 'var(--muted)' }}>{info.label}</div></div>
                    <span style={{ fontSize: 10, color: info.color, padding: '2px 8px', borderRadius: 20, background: `${info.color}15`, border: `1px solid ${info.color}30`, flexShrink: 0 }}>verbunden</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {error && <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{error}</p>}
        {!status?.connected ? (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>Verbinde deine Social-Media-Konten.</p>
            <button onClick={handleSetup} disabled={setting} className="btn-primary" style={{ fontSize: 13 }}>{setting ? <><Spinner /> Weiterleitung...</> : '⊹ Kanäle verbinden'}</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: 8, flex: 1, minWidth: 200 }}>
              <span className="dot-live" /><span style={{ fontSize: 12, color: 'var(--green)' }}>Profil: {status.username}</span>
            </div>
            <button onClick={handleSetup} disabled={setting} className="btn-ghost" style={{ fontSize: 12 }}>{setting ? <><Spinner /> Weiterleitung...</> : '+ Kanäle verwalten'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Blog-CMS Tab ──────────────────────────────────────────────────────────────
function CmsTab({ token }) {
  const [form, setForm] = useState({ cms_url: '', cms_email: '', cms_password: '', site_slug: 'look4more', collection_slug: 'posts' });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');

  useEffect(() => {
    api('/cms/settings', {}, token)
      .then(d => { if (d) setForm(prev => ({ ...prev, ...d, cms_password: '' })); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSave() {
    if (!form.cms_url || !form.cms_email || !form.cms_password) {
      setError('URL, E-Mail und Passwort sind Pflichtfelder.'); return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      await api('/cms/settings', { method: 'POST', body: JSON.stringify(form) }, token);
      setSuccess('Verbindung gespeichert.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleTest() {
    setTesting(true); setError(''); setSuccess('');
    try {
      await api('/cms/test', { method: 'POST' }, token);
      setSuccess('Verbindung erfolgreich — Login funktioniert.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) { setError(e.message); } finally { setTesting(false); }
  }

  if (loading) return <div className="shimmer" style={{ height: 300, borderRadius: 12 }} />;

  return (
    <div className="fade-in">
      <div className="glass" style={{ padding: 22, marginBottom: 14 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Payload CMS Verbindung</h2>
          <p style={{ fontSize: 12, color: 'var(--text2)' }}>Blog-Entwürfe direkt in Payload CMS veröffentlichen.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.08em' }}>CMS URL</span>
            <input
              value={form.cms_url}
              onChange={e => setForm(p => ({ ...p, cms_url: e.target.value }))}
              placeholder="https://admin.example.de"
              className="input" style={{ fontSize: 13 }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.08em' }}>E-MAIL</span>
              <input
                value={form.cms_email}
                onChange={e => setForm(p => ({ ...p, cms_email: e.target.value }))}
                placeholder="admin@example.de"
                className="input" style={{ fontSize: 13 }}
                autoComplete="username"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.08em' }}>PASSWORT</span>
              <input
                value={form.cms_password}
                onChange={e => setForm(p => ({ ...p, cms_password: e.target.value }))}
                type="password"
                placeholder="●●●●●●●●"
                className="input" style={{ fontSize: 13 }}
                autoComplete="current-password"
              />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.08em' }}>SITE SLUG</span>
              <input
                value={form.site_slug}
                onChange={e => setForm(p => ({ ...p, site_slug: e.target.value }))}
                placeholder="look4more"
                className="input" style={{ fontSize: 13 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.08em' }}>COLLECTION</span>
              <input
                value={form.collection_slug}
                onChange={e => setForm(p => ({ ...p, collection_slug: e.target.value }))}
                placeholder="posts"
                className="input" style={{ fontSize: 13 }}
              />
            </label>
          </div>
        </div>
        {error && (
          <div style={{ padding: '10px 14px', background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.2)', borderRadius: 8, marginTop: 12 }}>
            <p style={{ color: 'var(--red)', fontSize: 12 }}>{error}</p>
          </div>
        )}
        {success && <p style={{ color: 'var(--green)', fontSize: 12, marginTop: 10 }}>✓ {success}</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ fontSize: 13 }}>
            {saving ? <><Spinner /> Speichere...</> : '▶ Speichern'}
          </button>
          <button onClick={handleTest} disabled={testing} className="btn-ghost" style={{ fontSize: 13 }}>
            {testing ? <><Spinner /> Teste...</> : '⊹ Verbindung testen'}
          </button>
        </div>
      </div>
      <div className="glass" style={{ padding: '14px 18px' }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>HINWEIS</div>
        <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
          Payload CMS 3.x — Login via E-Mail und Passwort. Posts werden als Entwurf angelegt
          und müssen im CMS freigegeben werden. Collection{' '}
          <code style={{ fontSize: 11, background: 'var(--glass2)', padding: '1px 5px', borderRadius: 4 }}>posts</code>{' '}
          für look4more, <code style={{ fontSize: 11, background: 'var(--glass2)', padding: '1px 5px', borderRadius: 4 }}>blog-posts</code>{' '}
          für Kundenprojekte.
        </p>
      </div>
    </div>
  );
}

// ── Darstellung Tab ───────────────────────────────────────────────────────────
function AppearanceTab({ isDark, toggleTheme }) {
  return (
    <div className="fade-in glass" style={{ padding: 24 }}>
      <h2 style={{ fontSize: 14, marginBottom: 20 }}>Darstellung</h2>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>Dark / Light Mode</div>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>Aktuell: {isDark ? 'Dark Mode' : 'Light Mode'}</div>
        </div>
        <button onClick={toggleTheme} style={{ width: 52, height: 28, borderRadius: 14, background: isDark ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg,var(--cyan),var(--blue))', border: '1px solid var(--glass-border2)', position: 'relative', cursor: 'pointer' }}>
          <div style={{ position: 'absolute', top: 3, left: isDark ? 3 : 24, width: 20, height: 20, borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.4)' : '#fff', transition: 'left 0.2s' }} />
        </button>
      </div>
    </div>
  );
}

// ── Account Tab ───────────────────────────────────────────────────────────────
function AccountTab({ user, token }) {
  const TIER_LABELS = { basic: 'Basic', pro: 'Pro', team: 'Team', agency: 'Agency' };
  const TIER_COLORS = { basic: '#00e5ff', pro: '#9944ff', team: '#00ff88', agency: '#ffaa00' };

  const [freshUser, setFreshUser] = useState(null);

  useEffect(() => {
    fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.id) setFreshUser(d); })
      .catch(() => {});
  }, [token]);

  const displayUser = freshUser || user;
  const tierColor   = TIER_COLORS[displayUser?.tier] || '#00e5ff';
  const tierLabel   = TIER_LABELS[displayUser?.tier] || (displayUser?.tier || 'Basic');

  const services = [
    { label: '✓ Textgenerierung',       color: 'var(--green)' },
    { label: '✓ Bildgenerierung',       color: 'var(--green)' },
    { label: '✓ Social Media Publishing', color: 'var(--cyan)' },
  ];

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="glass" style={{ padding: 24 }}>
        <h2 style={{ fontSize: 14, marginBottom: 16 }}>Account</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: `linear-gradient(135deg, ${tierColor}, #0044ff)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#000', flexShrink: 0 }}>
            {(displayUser?.name || displayUser?.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>{displayUser?.name || 'Kein Name'}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{displayUser?.email}</div>
            <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 20, background: `${tierColor}18`, color: tierColor, border: `1px solid ${tierColor}30`, letterSpacing: '0.05em', fontWeight: 600 }}>
              {tierLabel}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {services.map(s => (
            <span key={s.label} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: 'var(--glass2)', color: s.color, border: '1px solid var(--glass-border)' }}>{s.label}</span>
          ))}
        </div>
      </div>

      <UsageDisplay token={token} freshUser={freshUser} />

      <PasswordChangeCard token={token} />

      <div className="glass" style={{ padding: '14px 20px' }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 6 }}>SUPPORT</div>
        <p style={{ fontSize: 12, color: 'var(--text2)' }}>
          Fragen oder Änderungen: <a href="mailto:steffen.woik@look4more.de" style={{ color: 'var(--cyan)' }}>steffen.woik@look4more.de</a>
        </p>
      </div>
    </div>
  );
}

// ── Passwort ändern ───────────────────────────────────────────────────────────
function PasswordChangeCard({ token }) {
  const { user, login, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);

  async function handleLogoutAll() {
    if (!window.confirm('Auf allen Geräten abmelden? Du wirst danach auch hier ausgeloggt und musst dich neu anmelden.')) return;
    setLogoutAllLoading(true);
    try {
      await api('/auth/logout-all', { method: 'POST' }, token);
      logout();
    } catch (err) {
      setError(err.message);
      setLogoutAllLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess('');

    if (newPw.length < 8) return setError('Neues Passwort muss mindestens 8 Zeichen haben.');
    if (newPw !== confirmPw) return setError('Neue Passwörter stimmen nicht überein.');

    setLoading(true);
    try {
      const res = await api('/auth/change-password', { method: 'POST', body: JSON.stringify({ current_password: currentPw, new_password: newPw }) }, token);
      // Backend widerruft beim Passwortwechsel alle Sitzungen und gibt ein frisches Token
      // fuer diese Sitzung zurueck -- muss uebernommen werden, sonst wird man beim naechsten
      // Request selbst ausgeloggt.
      if (res?.token) login(res.token, user);
      setSuccess('Passwort geändert.');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="glass" style={{ padding: 24 }}>
      <h2 style={{ fontSize: 14, marginBottom: 16 }}>Passwort ändern</h2>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 340 }}>
        <input type="password" className="input" placeholder="Aktuelles Passwort" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required style={{ fontSize: 13 }} />
        <input type="password" className="input" placeholder="Neues Passwort (mind. 8 Zeichen)" value={newPw} onChange={e => setNewPw(e.target.value)} required style={{ fontSize: 13 }} />
        <input type="password" className="input" placeholder="Neues Passwort bestätigen" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required style={{ fontSize: 13 }} />

        {error   && <div style={{ padding: '9px 13px', borderRadius: 8, fontSize: 12, background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.2)', color: 'var(--red)' }}>{error}</div>}
        {success && <div style={{ padding: '9px 13px', borderRadius: 8, fontSize: 12, background: 'rgba(0,255,150,0.08)', border: '1px solid rgba(0,255,150,0.2)', color: 'var(--green)' }}>{success}</div>}

        <button type="submit" disabled={loading} className="btn-primary" style={{ padding: '10px', fontSize: 13, justifyContent: 'center' }}>
          {loading ? 'Speichere...' : 'Passwort speichern'}
        </button>
      </form>

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
          Verdacht, dass jemand anderes Zugriff auf deinen Account hat? Meldet alle Geräte ab, auf denen du gerade eingeloggt bist (auch dieses hier).
        </div>
        <button type="button" onClick={handleLogoutAll} disabled={logoutAllLoading} className="btn-ghost" style={{ padding: '8px 12px', fontSize: 12 }}>
          {logoutAllLoading ? 'Melde ab...' : 'Auf allen Geräten abmelden'}
        </button>
      </div>
    </div>
  );
}

// ── Nutzungs-Anzeige ──────────────────────────────────────────────────────────
function UsageDisplay({ token, freshUser }) {
  const [usage,   setUsage]   = useState(freshUser?.usage || null);
  const [loading, setLoading] = useState(!freshUser?.usage);

  useEffect(() => {
    if (freshUser?.usage) { setUsage(freshUser.usage); setLoading(false); return; }
    fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.usage) setUsage(d.usage); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [token, freshUser]);

  if (loading) return <div className="shimmer" style={{ height: 120, borderRadius: 12 }} />;
  if (!usage) return null;

  const items = [
    { label: 'Posts generiert',    used: usage.posts?.used     || 0, limit: usage.posts?.limit     || 999, color: 'var(--cyan)',  icon: '✦' },
    { label: 'Posts versendet',    used: usage.publishes?.used || 0, limit: usage.publishes?.limit || 999, color: 'var(--green)', icon: '⊹' },
    { label: 'Bilder generiert',   used: usage.images?.used    || 0, limit: usage.images?.limit    || 999, color: 'var(--gold)',  icon: '🖼' },
  ];

  return (
    <div className="glass" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Nutzung diese Woche</h2>
          <p style={{ fontSize: 11, color: 'var(--muted)' }}>ab {usage.week_start}</p>
        </div>
        <span className="dot-live" style={{ width: 5, height: 5 }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map(item => {
          const pct  = item.limit >= 999 ? 0 : Math.min((item.used / item.limit) * 100, 100);
          const full = item.limit < 999 && item.used >= item.limit;
          return (
            <div key={item.label}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13 }}>{item.icon}</span>
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>{item.label}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: full ? 'var(--red)' : item.color, fontFamily: 'Orbitron' }}>{item.used}</span>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>/ {item.limit >= 999 ? '∞' : item.limit}</span>
                  {item.limit < 999 && (
                    <span style={{ fontSize: 10, color: full ? 'var(--red)' : 'var(--muted)', padding: '1px 8px', borderRadius: 10, background: full ? 'rgba(255,0,85,0.08)' : 'var(--glass2)', border: `1px solid ${full ? 'rgba(255,0,85,0.2)' : 'var(--glass-border)'}` }}>
                      {full ? 'Limit erreicht' : `${item.limit - item.used} übrig`}
                    </span>
                  )}
                </div>
              </div>
              {item.limit < 999 && (
                <div style={{ height: 4, background: 'var(--glass-border)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: full ? 'var(--red)' : item.color, borderRadius: 2, transition: 'width 0.4s' }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Hilfskomponenten ──────────────────────────────────────────────────────────
function Spinner() {
  return <span style={{ width: 11, height: 11, border: '2px solid rgba(0,255,255,0.2)', borderTopColor: 'var(--cyan)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block', flexShrink: 0 }} />;
}

function ProfileTextEditor({ profile, setProfile, token }) {
  const [editing, setEditing] = useState(false);
  const [text,    setText]    = useState(profile.profile_text || '');
  const [saving,  setSaving]  = useState(false);

  async function save() {
    setSaving(true);
    try { const d = await api('/company/profile-text', { method: 'PATCH', body: JSON.stringify({ profile_text: text }) }, token); setProfile(d); setEditing(false); }
    catch {} finally { setSaving(false); }
  }

  if (!editing) return (
    <div>
      <p style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text2)', whiteSpace: 'pre-wrap', marginBottom: 10 }}>{profile.profile_text}</p>
      <button onClick={() => { setText(profile.profile_text); setEditing(true); }} className="btn-ghost" style={{ fontSize: 11 }}>✎ Bearbeiten</button>
    </div>
  );
  return (
    <div>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={8} className="input" style={{ lineHeight: 1.75, fontSize: 13, resize: 'vertical', marginBottom: 10 }} autoFocus />
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} className="btn-primary" style={{ fontSize: 12 }}>{saving ? 'Speichere...' : '✓ Speichern'}</button>
        <button onClick={() => setEditing(false)} className="btn-ghost" style={{ fontSize: 12 }}>Abbrechen</button>
      </div>
    </div>
  );
}

// ── Analytics Tab (GA4) ───────────────────────────────────────────────────────
function AnalyticsTab({ token }) {
  const [propertyId,  setPropertyId]  = useState('');
  const [connected,   setConnected]   = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [testing,     setTesting]     = useState(false);
  const [connecting,  setConnecting]  = useState(false);
  const [success,     setSuccess]     = useState('');
  const [error,       setError]       = useState('');

  useEffect(() => {
    // GA4 OAuth-Callback erkennen
    const params = new URLSearchParams(window.location.search);
    if (params.get('ga4') === 'connected') {
      setSuccess('Google Analytics erfolgreich verbunden. Bitte Property ID eintragen.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('ga4') === 'error') {
      const reason = params.get('reason');
      if (reason === 'no_refresh_token') {
        setError('Kein Refresh-Token erhalten. Bitte erneut verbinden und alle Berechtigungen erteilen.');
      } else {
        setError('Verbindung fehlgeschlagen: ' + (reason || 'Unbekannter Fehler'));
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    api('/ga4/settings', {}, token)
      .then(d => { if (d?.property_id) setPropertyId(d.property_id); if (d?.connected) setConnected(true); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  async function handleConnect() {
    setConnecting(true); setError('');
    try {
      const { url } = await api('/ga4/connect-url', {}, token);
      window.location.href = url;
    } catch (e) { setError(e.message); setConnecting(false); }
  }

  async function handleDisconnect() {
    if (!window.confirm('Google-Verbindung trennen?')) return;
    try {
      await api('/ga4/disconnect', { method: 'POST' }, token);
      setConnected(false); setSuccess('Verbindung getrennt.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.message); }
  }

  async function handleSavePropertyId() {
    if (!propertyId.trim()) { setError('Property ID ist erforderlich.'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      await api('/ga4/settings', { method: 'POST', body: JSON.stringify({ property_id: propertyId }) }, token);
      setSuccess('Property ID gespeichert.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleTest() {
    setTesting(true); setError(''); setSuccess('');
    try {
      await api('/ga4/test', { method: 'POST' }, token);
      setSuccess('GA4-Verbindung erfolgreich — Daten abrufbar.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) { setError(e.message); } finally { setTesting(false); }
  }

  if (loading) return <div className="shimmer" style={{ height: 300, borderRadius: 12 }} />;

  return (
    <div className="fade-in">
      <div className="glass" style={{ padding: 22, marginBottom: 14 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Google Analytics 4</h2>
          <p style={{ fontSize: 12, color: 'var(--text2)' }}>Website-Traffic direkt im Dashboard anzeigen.</p>
        </div>

        {/* Verbindungsstatus */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: connected ? 'rgba(0,255,136,0.05)' : 'rgba(255,255,255,0.03)', border: `1px solid ${connected ? 'rgba(0,255,136,0.2)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 10, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>G</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: connected ? 'var(--green)' : 'var(--text)' }}>{connected ? 'Google Analytics verbunden' : 'Nicht verbunden'}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{connected ? 'OAuth 2.0 · Lesezugriff aktiv' : 'Klicke "Mit Google verbinden" um zu starten'}</div>
            </div>
          </div>
          {connected
            ? <button onClick={handleDisconnect} className="btn-ghost" style={{ fontSize: 11, padding: '4px 12px' }}>Trennen</button>
            : <button onClick={handleConnect} disabled={connecting} className="btn-primary" style={{ fontSize: 12 }}>{connecting ? <><Spinner /> Weiterleitung...</> : '⊹ Mit Google verbinden'}</button>
          }
        </div>

        {/* Property ID */}
        {connected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 16 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.08em' }}>GA4 PROPERTY ID</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={propertyId}
                onChange={e => setPropertyId(e.target.value.replace(/^properties\//, ''))}
                placeholder="123456789"
                className="input" style={{ fontSize: 13, flex: 1 }}
              />
              <button onClick={handleSavePropertyId} disabled={saving} className="btn-primary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                {saving ? '...' : '▶ Speichern'}
              </button>
            </div>
            <span style={{ fontSize: 10, color: 'var(--muted)' }}>GA4 Admin → Property Settings → Property ID</span>
          </div>
        )}

        {error && <div style={{ padding: '10px 14px', background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.2)', borderRadius: 8, marginBottom: 12 }}><p style={{ color: 'var(--red)', fontSize: 12 }}>{error}</p></div>}
        {success && <p style={{ color: 'var(--green)', fontSize: 12, marginBottom: 12 }}>✓ {success}</p>}

        {connected && propertyId && (
          <button onClick={handleTest} disabled={testing} className="btn-ghost" style={{ fontSize: 13 }}>
            {testing ? <><Spinner /> Teste...</> : '◈ Verbindung testen'}
          </button>
        )}
      </div>

      {!connected && (
        <div className="glass" style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 10 }}>EINRICHTUNG (2 SCHRITTE)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              ['1', 'Mit Google verbinden', 'Klicke oben auf "Mit Google verbinden" und melde dich mit dem Google-Konto an, das Zugriff auf deine GA4-Property hat.'],
              ['2', 'Property ID eintragen', 'Nach der Verbindung: Google Analytics → Admin → Property Settings → numerische Property ID eintragen.'],
            ].map(([n, title, desc]) => (
              <div key={n} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: 'var(--glass2)', borderRadius: 8, border: '1px solid var(--glass-border)' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--cyan-dim)', border: '1px solid rgba(0,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--cyan)', flexShrink: 0 }}>{n}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { PLATFORMS_INFO };
